import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  Textarea,
} from "../../shared/ui";
import type {
  PageResponse,
  SprintSummary,
  TeamSummary,
} from "../../shared/types/api";

/**
 * Edits a sprint that has not started, and sharpens the goal of one that has.
 *
 * <p>The two are the same panel because they are the same intent — "this sprint is not quite
 * right" — and different fields because a running sprint has already measured itself against its
 * dates. Its capacity was worked out from them and its commitment was captured on the day it
 * started; moving them afterwards would leave a record describing a sprint that never happened.
 *
 * <p>The goal is the exception, and deliberately so. A sprint whose goal is "finish the tickets"
 * has none, and its review has nothing to be held against — so it stays editable right up until
 * the sprint closes, on the theory that a goal written down late is worth more than one never
 * written at all.
 */
export function EditSprintDrawer({
  sprint,
  onClose,
  onSaved,
}: {
  sprint: SprintSummary | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("");
  const [teamId, setTeamId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Only what a planned sprint can still change is asked for; a running one gets the goal alone.
  const planning = sprint?.status === "PLANNED";

  const teams = useQuery({
    queryKey: ["teams", "picker"],
    queryFn: () =>
      api.get<PageResponse<TeamSummary>>(
        "/api/v1/teams?size=200&sort=name,asc",
      ),
    enabled: Boolean(sprint) && planning,
  });

  useEffect(() => {
    if (sprint) {
      setName(sprint.name);
      setGoal(sprint.goal ?? "");
      setTeamId(sprint.teamId ?? "");
      setStartDate(sprint.startDate);
      setEndDate(sprint.endDate);
      setError(null);
    }
  }, [sprint]);

  const backwards = Boolean(startDate && endDate) && endDate < startDate;

  const submit = async () => {
    if (!sprint) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (planning) {
        await api.patch(`/api/v1/sprints/${sprint.id}`, {
          name,
          goal: goal || null,
          teamId: teamId || null,
          startDate,
          endDate,
          version: sprint.version,
        });
      } else {
        await api.post(`/api/v1/sprints/${sprint.id}/goal`, {
          goal: goal || null,
        });
      }
      onSaved();
      onClose();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : "Could not save the sprint.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={Boolean(sprint)}
      onClose={onClose}
      title={planning ? "Edit sprint" : "Set the sprint goal"}
      subtitle={sprint?.name}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => void submit()}
            loading={saving}
            disabled={planning && (name.trim() === "" || backwards)}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        {!planning && (
          <InlineAlert tone="info">
            This sprint has started, so its dates and team are fixed — its
            capacity was worked out from them and its commitment was captured on
            the day it began. The goal can still be sharpened, and is worth
            writing down late rather than not at all.
          </InlineAlert>
        )}

        {planning && (
          <Field label="Name" required>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </Field>
        )}

        <Field
          label="Sprint goal"
          hint="One sentence about what this sprint is for. The review is held against it."
        >
          <Textarea
            rows={2}
            autoFocus={!planning}
            value={goal}
            onChange={(event) => setGoal(event.target.value)}
            placeholder="Customers can pay by card end to end, including refunds."
          />
        </Field>

        {planning && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Starts" required>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                />
              </Field>
              <Field label="Ends" required>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                />
              </Field>
            </div>

            {backwards && (
              <InlineAlert tone="warning">
                A sprint cannot end before it starts.
              </InlineAlert>
            )}

            <Field
              label="Team"
              hint="Setting it lets a project run one sprint per team, and scopes the capacity calculation to that team."
            >
              <Select
                value={teamId}
                onChange={(event) => setTeamId(event.target.value)}
              >
                <option value="">Everyone on the project</option>
                {(teams.data?.content ?? []).map((team) => (
                  <option key={team.id} value={team.id}>
                    {team.name}
                  </option>
                ))}
              </Select>
            </Field>

            <InlineAlert tone="info">
              Everything here can still change because nothing has been measured
              yet. Once the sprint starts, its commitment and capacity are
              captured and only the goal stays editable.
            </InlineAlert>
          </>
        )}
      </div>
    </Drawer>
  );
}
